import axios from 'axios';

const API_URL = '';

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 segundos (PDF pode demorar)
});

// ✅ Interceptor para adicionar token automaticamente
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Lê o header X-Furos-Avisos de uma resposta de PDF (furos que não puderam ser
// transformados na rotação/espelhamento e ficaram na posição original). O valor
// é uma lista de strings (PDF único) ou um dict { peca: [avisos] } (lote legado).
// Retorna { avisos, total } — `avisos` é o valor cru parseado, `total` a contagem
// de mensagens (para decidir se sinaliza). Nunca lança: header ausente/ilegível
// vira { avisos: null, total: 0 }.
export const lerAvisosFuros = (response) => {
  const raw = response?.headers?.['x-furos-avisos'];
  if (!raw) return { avisos: null, total: 0 };
  try {
    const parsed = JSON.parse(raw);
    const total = Array.isArray(parsed)
      ? parsed.length
      : Object.values(parsed).reduce(
          (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
          0,
        );
    return { avisos: parsed, total };
  } catch (err) {
    console.error('lerAvisosFuros: header X-Furos-Avisos ilegível:', err);
    return { avisos: null, total: 0 };
  }
};

// Gerar PDF
export const generatePDF = async (file, config) => {
  const formData = new FormData();
  formData.append('file', file);
  
  // Converter bordas para JSON string
  const bordasJson = config.bordas ? JSON.stringify(config.bordas) : '{}';
  
  // Adicionar configurações como query params
  const params = new URLSearchParams({
    angulo_rotacao: config.angulo_rotacao || 0,
    espelhar_peca: config.espelhar_peca || false,
    bordas: bordasJson,
    revisao: config.revisao || '',
    alerta: config.alerta || '',
    status: config.status || 'CÓPIA CONTROLADA'
  });
  
  const response = await api.post(`/generate-pdf?${params}`, formData, {
    responseType: 'blob',
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return { blob: response.data, ...lerAvisosFuros(response) };
};

/**
 * Gerar múltiplos PDFs em lote e retornar ZIP
 */
export const generatePDFBatch = async (arquivos, onProgress) => {
  const formData = new FormData();
  
  // Adicionar todos os arquivos
  arquivos.forEach((arq, index) => {
    formData.append('files', arq.file);
  });
  
  // Adicionar configurações como JSON - CORRIGIDO
  const configs = arquivos.map(arq => ({
    angulo_rotacao: arq.config.angulo_rotacao || 0,
    espelhar_peca: arq.config.espelhar_peca || false,
    bordas: arq.config.bordas || { top: null, bottom: null, left: null, right: null },
    revisao: arq.config.revisao || '',
    alerta: arq.config.alerta || '',
    status: arq.config.status || 'CÓPIA CONTROLADA'    
  }));

  console.log('📤 Enviando configs:', configs);
  formData.append('configs', JSON.stringify(configs));
  
  try {
    const response = await axios.post(
      `${API_URL}/generate-pdf-batch`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // Estimativa de progresso
            const current = Math.floor((percentCompleted / 100) * arquivos.length);
            onProgress(current, arquivos.length);
          }
        }
      }
    );
    
    // Chamar progresso final
    if (onProgress) {
      onProgress(arquivos.length, arquivos.length);
    }

    return { blob: response.data, ...lerAvisosFuros(response) };
  } catch (error) {
    console.error('Erro ao gerar PDFs em lote:', error);
    throw error;
  }
};

/**
 * Agrupa furos verticais em sequência no mesmo eixo
 * Retorna { furos_verticais, furos_horizontais } com grupos compactados
 */
export const agruparFuros = async (furosVerticais, furosHorizontais = []) => {
  const response = await api.post('/editor/agrupar-furos', {
    furos_verticais: furosVerticais,
    furos_horizontais: furosHorizontais,
  });
  return response.data;
};

/**
 * Exportar peça do editor como MPR
 */
export const exportarMPR = async (pecaData) => {
  try {
    const response = await api.post(
      '/editor/export-mpr',
      pecaData,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    // Tentar ler detalhe do erro a partir do blob
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        const enhanced = new Error(json.detail || text);
        enhanced.response = error.response;
        throw enhanced;
      } catch (parseErr) {
        if (parseErr.response) throw parseErr;
      }
    }
    throw error;
  }
};

/**
 * Faz o parse de um arquivo .mpr no backend (fonte única de parse de MPR do
 * sistema) e devolve furos/dimensões em JSON, no mesmo formato usado ao
 * abrir uma peça do banco (furos.verticais/.horizontais).
 */
export const parseMPR = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/editor/parse-mpr', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Retorna os títulos das páginas que serão geradas, sem gerar o PDF.
 * Usado para configurar alertas por página antes de gerar.
 */
export const previewPaginas = async (pecaData) => {
  const formData = new FormData();
  formData.append('espessura', pecaData.espessura || 15);
  formData.append('furos_verticais', JSON.stringify(pecaData.furos || []));
  formData.append('furos_horizontais', JSON.stringify(pecaData.furosHorizontais || []));

  const response = await axios.post(`${API_URL}/editor/preview-paginas`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data; // { paginas: [{titulo, tipo}] }
};

/**
 * Gerar PDF direto do editor
 */
export const gerarPDFEditor = async (pecaData) => {
  try {
    const formData = new FormData();

    // Dados da peça
    formData.append('largura', pecaData.largura || 0);
    formData.append('comprimento', pecaData.comprimento || 0);
    formData.append('espessura', pecaData.espessura || 15);
    formData.append('nome_peca', pecaData.nome || 'Peça sem nome');
    formData.append('alertas', JSON.stringify(pecaData.alertas || {}));
    formData.append('versao', pecaData.versao || '01');

    // NOVO: ID da peça (se existir)
    if (pecaData.peca_id) {
      formData.append('peca_id', pecaData.peca_id);
    }
    
    // Furos verticais
    const furosVerticais = pecaData.furos || [];
    formData.append('furos_verticais', JSON.stringify(furosVerticais));
    
    // Furos horizontais
    const furosHorizontais = pecaData.furosHorizontais || [];
    formData.append('furos_horizontais', JSON.stringify(furosHorizontais));

    // Bordas
    const bordasData = pecaData.bordas || { topo: 'nenhum', baixo: 'nenhum', esquerda: 'nenhum', direita: 'nenhum' };
    formData.append('bordas', JSON.stringify(bordasData));

    // Transformação
    const transformacaoData = pecaData.transformacao || { rotacao: 0, espelhado: false };
    formData.append('transformacao', JSON.stringify(transformacaoData));

    // Rasgos
    formData.append('rasgos', JSON.stringify(pecaData.rasgos || []));

    const response = await axios.post(
      `${API_URL}/editor/generate-pdf`,
      formData,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return { blob: response.data, ...lerAvisosFuros(response) };
  } catch (error) {
    console.error('Erro ao gerar PDF do editor:', error);
    throw error;
  }
};

export const gerarPDFUsinagem = async (pecaData) => {
  try {
    const formData = new FormData();
    formData.append('largura',     pecaData.largura     || 0);
    formData.append('comprimento', pecaData.comprimento || 0);
    formData.append('espessura',   pecaData.espessura   || 15);
    formData.append('nome_peca',   pecaData.nome        || 'Peça sem nome');
    formData.append('observacoes', pecaData.observacoes || '');
    formData.append('rasgos',      JSON.stringify(pecaData.rasgos || []));
    if (pecaData.peca_id) {
      formData.append('peca_id', pecaData.peca_id);
    }

    const response = await axios.post(
      `${API_URL}/editor/generate-pdf-usinagem`,
      formData,
      {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao gerar PDF de usinagem:', error);
    throw error;
  }
};

export async function listarRegrasEspeciais(pecaId) {
  const response = await api.get(`/pecas/${pecaId}/regras-especiais`);
  return response.data;
}

export async function criarRegraEspecial(pecaId, dados) {
  const response = await api.post(`/pecas/${pecaId}/regras-especiais`, dados);
  return response.data;
}

export async function deletarRegraEspecial(pecaId, regraId) {
  await api.delete(`/pecas/${pecaId}/regras-especiais/${regraId}`);
}

export async function getBordas(pecaId) {
  const response = await api.get(`/pecas/${pecaId}/bordas`);
  return response.data;
}

export async function upsertBordas(pecaId, bordas) {
  const _tr = (v) =>
    v === 'cor'   ? { estado: 'fitada', tipo_fita: 'cor'   } :
    v === 'pardo' ? { estado: 'fitada', tipo_fita: 'pardo' } :
                    { estado: 'crua' };
  const response = await api.post(`/pecas/${pecaId}/bordas`, {
    topo:     _tr(bordas.topo),
    baixo:    _tr(bordas.baixo),
    esquerda: _tr(bordas.esquerda),
    direita:  _tr(bordas.direita),
  });
  return response.data;
}

export async function sugerirPosicionamento(pecaId, pecaOriginal, espessura, bordaBatente = null) {
  const response = await api.post(`/pecas/${pecaId}/posicionamento/sugerir`, {
    comprimento: pecaOriginal.comprimento,
    largura: pecaOriginal.largura,
    espessura,
    furos: (pecaOriginal.furos || []).map(f => ({
      x: f.x,
      y: f.y,
      diametro: f.diametro,
      profundidade: f.profundidade,
      lado: f.lado,
      uid: f.uid,
    })),
    furosHorizontais: (pecaOriginal.furosHorizontais || []).map(f => ({
      x: f.x,
      y: f.y,
      z: f.z,
      diametro: f.diametro,
      profundidade: f.profundidade,
      lado: f.lado,
      uid: f.uid,
    })),
    ...(bordaBatente ? { borda_batente_forcada: bordaBatente } : {}),
  });
  return response.data;
}

export async function aceitarPosicionamento(pecaId, pecaOriginal, espessura, bordaBatente, plano, rotacaoAceita) {
  const response = await api.post(`/pecas/${pecaId}/posicionamento/aceitar`, {
    comprimento: pecaOriginal.comprimento,
    largura: pecaOriginal.largura,
    espessura,
    furos: (pecaOriginal.furos || []).map(f => ({
      x: f.x,
      y: f.y,
      diametro: f.diametro,
      profundidade: f.profundidade,
      lado: f.lado,
      uid: f.uid,
    })),
    furosHorizontais: (pecaOriginal.furosHorizontais || []).map(f => ({
      x: f.x,
      y: f.y,
      z: f.z,
      diametro: f.diametro,
      profundidade: f.profundidade,
      lado: f.lado,
      uid: f.uid,
    })),
    ...(bordaBatente ? { borda_batente_forcada: bordaBatente } : {}),
    plano_sugerido: plano,
    rotacao_aceita: rotacaoAceita,
  });
  return response.data;
}

export const adminAPI = {
  listarUsuarios: () => api.get('/api/admin/usuarios'),
  criarUsuario: (data) => api.post('/api/admin/usuarios', data),
  gerarLinkReset: (id) => api.post(`/api/admin/usuarios/${id}/reset-link`),
  alternarAtivo: (id) => api.put(`/api/admin/usuarios/${id}/ativo`),
};

export const userAPI = {
  getMe: () => api.get('/auth/me'),
  acceptInvite: (token, senha) =>
    axios.post(`${API_URL}/auth/accept-invite`, { token, senha }),
  changePassword: (senhaAtual, novaSenha) =>
    api.post('/auth/change-password', { senha_atual: senhaAtual, nova_senha: novaSenha }),
  updateProfile: (data) => api.patch('/auth/me', data),
};

export default api;